import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import ServiceCard from "../components/ServiceCard";
import GallerySection from "../components/GallerySection";
import GoogleReviews from "../components/GoogleReviews";
import FAQSection from "../components/FAQSection";
import ContactForm from "../components/ContactForm";
import { getCMSCollection, getOptimizedImageUrl, getGalleryByRef } from "../lib/cms";

export default function Home() {
  const [pages, setPages] = useState([]);
  const [services, setServices] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    async function loadData() {
      const [pagesData, servicesData, galleriesData, reviewsData, faqsData] =
        await Promise.all([
          getCMSCollection("pages"),
          getCMSCollection("services"),
          getCMSCollection("galleries"),
          getCMSCollection("reviews"),
          getCMSCollection("faqs"),
        ]);
      setPages(pagesData);
      setServices(servicesData);
      setGalleries(galleriesData);
      setReviews(reviewsData);
      setFaqs(faqsData);
    }
    loadData();
  }, []);

  const pageData =
    pages.find((p) => {
      const slugVal = typeof p.slug === "object" ? p.slug?._ : p.slug;
      return slugVal === "page-hjem" || slugVal === "hjem" || p.title === "Caspers Træservice";
    }) || {};

  const heroGallery = getGalleryByRef(galleries, "gallery-forside") || getGalleryByRef(galleries, "forside-galleri");
  const galleryImages = heroGallery?.images || [];

  return (
    <>
      <SEO
        title="Caspers Træservice"
        description="Professionel og sikker træfældning af alle typer træer – fra simple opgaver til risikofyldte fældninger tæt på bygninger. Erfaring og uddannelse sikrer en veludført opgave."
        ogImage="https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/p-20230223-091955.jpeg"
      />

      {/* HERO SECTION — Call To Action front and center in first viewport */}
      <section className="relative bg-[#10241A] text-white overflow-hidden py-16 sm:py-24 lg:py-28 border-b border-[#1E4231]">
        {/* Background Image with Deep Natural Gradient */}
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(
              "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/p-20230223-091955.jpeg",
              1920,
              85
            )}
            alt="Caspers Træservice professionel fældning"
            className="w-full h-full object-cover object-center brightness-90"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0C1A12]/90 via-[#10241A]/75 to-[#10241A]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#10241A]/90 via-transparent to-black/30" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.15]">
              Sikker og professionel{" "}
              <span className="text-[#F3EBE0] underline decoration-[#D9691E] decoration-4 underline-offset-8">
                træfældning og beskæring
              </span>
            </h1>

            {/* Subtitle verbatim from scrape */}
            <p className="text-base sm:text-lg md:text-xl text-[#C6DDD1] leading-relaxed max-w-2xl font-normal">
              Professionel og sikker træfældning af alle typer træer – fra simple opgaver til risikofyldte fældninger tæt på bygninger. Erfaring og uddannelse sikrer en veludført opgave.
            </p>

            {/* FRONT AND CENTER CALL TO ACTION */}
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              {/* Primary Direct Call CTA */}
              <a
                href="tel:42169230"
                className="inline-flex items-center justify-center gap-3 bg-[#D9691E] hover:bg-[#C25B15] text-white font-extrabold text-base sm:text-lg px-7 py-4 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-200 active:scale-[0.98] cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-white"
              >
                <i className="fa-solid fa-phone text-lg animate-pulse" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring 42 16 92 30</span>
              </a>

              {/* Secondary Contact Form CTA */}
              <a
                href="#kontakt-formular"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-base px-6 py-4 rounded-xl border border-white/20 backdrop-blur-sm transition-all duration-200 cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#D9691E]"
              >
                <span>Få et uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PRIMARY SERVICES SECTION — Træfældning and Beskæring prioritized */}
      <section className="py-16 sm:py-24 bg-[#FBF9F5]" id="servicer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#12281D] tracking-tight">
              Servicer
            </h2>
            <p className="mt-3 text-base sm:text-lg text-[#4E685A] leading-relaxed">
              Træfældning og beskæring udført professionelt og med omtanke. Tilpasset dine behov og omgivelser.
            </p>
            <div className="w-16 h-1 bg-[#D9691E] mx-auto mt-4 rounded-full" />
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <ServiceCard
              title="Træfældning"
              tagline=""
              teaser="Fra enkle fældninger til komplekse og risikofyldte opgaver. Udføres med fældning i fuld længde eller kontrolleret sektionsfældning via træklatring. Mulighed for komplet oprydning, flishugning og bortkørsel."
              image="https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/480536134-621057160721189-262596540666825768-n.jpeg"
              linkUrl="/traefaeldning"
            />

            <ServiceCard
              title="Beskæring af træer"
              tagline=""
              teaser="Kroneløft for mere lys og frihøjde eller kronereducering for at mindske træets omfang. Alt udføres skånsomt og fagligt korrekt med fleksible løsninger til oprydning og flishugning i haven."
              image="https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/472335433-602814825870936-6000153368973253049-n.jpeg"
              linkUrl="/beskaering"
            />

            <ServiceCard
              title="Rådgivning"
              tagline=""
              teaser="Få en ærlig og professionel vurdering fra en erfaren fagmand. Klar besked og konkret rådgivning, så du kan træffe den rigtige beslutning og undgå unødvendige udgifter."
              image="https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/540320308-772834888876748-8893434418948825966-n.jpeg"
              linkUrl="/raadgivning"
            />

            <ServiceCard
              title="Opsætning af redekasser"
              tagline=""
              teaser="Giv fuglene et sikkert sted at bo – også højt oppe i træerne. Med professionelt klatreudstyr kan jeg komme sikkert op i træet og placere kassen, hvor den passer bedst til formålet."
              image="https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/redekasse-klatreudstyr.webp"
              linkUrl="/opsaetning-af-redekasser"
            />
          </div>
        </div>
      </section>

      {/* OM VIRKSOMHEDEN — Verbatim presentation with modern Scandinavian layout */}
      <section className="py-16 sm:py-24 bg-[#F2EDE2]/70 border-y border-[#E2D8C8] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            {/* Image Side: Bredere og højere, placeret bag ved tekstboksen med overlap */}
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0">
              <div className="relative rounded-2xl overflow-hidden h-[420px] sm:h-[500px] lg:h-[620px] w-full">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/480536134-621057160721189-262596540666825768-n_fdad9abc.webp",
                    1200,
                    85
                  )}
                  alt="Caspers Træservice Casper Hartung-Struer"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Content Side: Boks med baggrundsfarve #1e5d31, ingen shadows eller border, overlapper billedet foran */}
            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10 bg-[#1e5d31] rounded-2xl p-6 sm:p-9 lg:p-12 space-y-6 shadow-none border-0">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F39C5A]">
                <span>Lokal fagmand i Sønderjylland</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Om virksomheden
              </h2>

              <div className="space-y-4 text-base sm:text-lg text-[#D5E5DB] leading-relaxed font-normal">
                <p>
                  Caspers Træservice er en lille virksomhed der har specialiseret sig i fældning og beskæring af træer.
                </p>
                <p>
                  Jeg er professionelt uddannet med over 10 års erfaring i træfældning, topkapning og beskæring.
                </p>
                <p className="font-semibold text-white">
                  Erfaring og uddannelse er din tryghed for et veludført og omhyggeligt stykke arbejde udført sikkert og professionelt.
                </p>
                <p>
                  Caspers Træservice er hjemmehørende i det sønderjyske og med udgangspunkt i Fole ved Gram dækkes hele Syddanmark.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap gap-4">
                <a
                  href="tel:42169230"
                  className="inline-flex items-center gap-2 bg-[#D9691E] hover:bg-[#c35c17] text-white px-5 py-3 rounded-xl font-bold text-sm transition-all whitespace-nowrap shadow-none"
                >
                  <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring: 42 16 92 30</span>
                </a>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-white hover:bg-[#F9F7F3] text-[#14291E] px-5 py-3 rounded-xl font-bold text-sm transition-all shadow-none"
                >
                  <span>Læs profil og kontakt</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS SECTION */}
      <GoogleReviews reviews={reviews} />

      {/* FAQ SECTION */}
      <FAQSection faqs={faqs} />

      {/* SELECTED PROJECTS / GALLERI SECTION */}
      <GallerySection
        title="Galleri"
        subtitle="Udførte træserviceopgaver i Syddanmark — klik på et billede for at se i fuld størrelse"
        images={galleryImages}
        className="bg-[#FBF9F5]"
      />

      {/* KONTAKT SECTION (with contact form, phone, email, address) */}
      <section className="py-16 sm:py-24 bg-[#14291E] text-white relative overflow-hidden" id="kontakt-formular">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0 space-y-6 lg:pr-8">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight lg:max-w-[75%]">
                Er du i tvivl om et træs tilstand?
              </h2>
              <p className="text-base text-[#BFD9CA] leading-relaxed max-w-lg lg:max-w-[75%]">
                Send et billede eller en beskrivelse af træet. Jeg giver en professionel vurdering og hjælper dig med den tryggeste løsning.
              </p>

              {/* Direct Info Cards - unified single box overlapping slightly under form to the right */}
              <div className="bg-[#1C3B2C] p-6 sm:p-7 rounded-2xl border border-[#2B543F] space-y-4 relative z-0 lg:-mr-8 lg:pr-14 shadow-none">
                <div className="flex items-center gap-4 pb-4 border-b border-white/10 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                      Ring direkte
                    </span>
                    <a
                      href="tel:42169230"
                      className="text-lg font-extrabold text-white hover:text-[#F39C5A] transition-colors whitespace-nowrap"
                    >
                      42 16 92 30
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 pb-4 border-b border-white/10 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-regular fa-envelope text-[#D9691E] text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                      Send en email
                    </span>
                    <a
                      href="mailto:info@casperstraeservice.dk"
                      className="text-base font-bold text-white hover:text-[#F39C5A] transition-colors whitespace-nowrap"
                    >
                      info@casperstraeservice.dk
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-solid fa-location-dot text-[#D9691E] text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                      Adresse
                    </span>
                    <span className="text-sm font-medium text-white block">
                      Caspers Træservice • Folevej 23, 6510 Gram
                    </span>
                    <span className="text-xs text-[#8DB59E]">CVR: 37232521</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10">
              <ContactForm defaultService="Rådgivning" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
